import React from "react";
import { Box, Flex, LoadingOverlay, Menu, Text } from "@mantine/core";
import {
  IconFileText,
  IconAlertTriangle,
  IconCircleX,
  IconSearch,
  IconPlus,
  IconSettings,
  IconArrowRight,
  IconChevronDown,
  IconFileDescription,
  IconSparkles,
} from "@tabler/icons-react";
import useSharedStyles from "../sharedStyles";
import { REPORT_SOURCE, REPORT_STATUS } from "../theme";
import StatusPill from "../components/StatusPill";
import RowActionMenu from "../components/RowActionMenu";

const PAGE_SIZE = 10;

// Fully server-driven: `reports` is already the current page for the given
// status/search/page — no client-side re-filtering or re-paging here, so
// results beyond the first 10 total reports are reachable.
const ReportsListView = ({
  reports,
  totalReports,
  status,
  search,
  page,
  onStatusChange,
  onSearchChange,
  onPageChange,
  kpi,
  onGenerate,
  onCreateManual,
  manualEnabled,
  onOpenSettings,
  onOpenPastDue,
  onOpenFailed,
  onOpenReport,
  onDeleteReport,
  deletingId,
  downloadingId,
  refreshing,
}) => {
  const { classes, cx } = useSharedStyles();
  const pageRows = reports;
  const totalPages = Math.max(1, Math.ceil((totalReports || 0) / PAGE_SIZE));

  return (
    <Box className={classes.content}>
      <Flex className={classes.toolbarCard}>
        {/* Two document types share one entry point: a hand-entered review for
            a provider documenting in its own EHR, and the Tara Ai draft built
            from approved Ogiso notes. Manual is hidden entirely when it's
            turned off in report settings. */}
        {manualEnabled ? (
          <Menu position="bottom-start" shadow="md" width={262}>
            <Menu.Target>
              <button className={classes.btnAdd}>
                <Flex align="center" gap={8}>
                  <IconPlus size={16} />
                  Create
                  <IconChevronDown size={15} stroke={2.4} />
                </Flex>
              </button>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Item icon={<IconFileDescription size={15} />} onClick={onCreateManual}>
                Quarterly Report
              </Menu.Item>
              <Menu.Divider />
              <Menu.Item icon={<IconSparkles size={15} color="#7856FF" />} onClick={onGenerate}>
                Generate Quarterly with Tara
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        ) : (
          <button className={classes.btnAdd} onClick={onGenerate}>
            <Flex align="center" gap={8}>
              <IconPlus size={16} />
              Generate Quarterly
            </Flex>
          </button>
        )}
        <button className={classes.iconBtnSquare} onClick={onOpenSettings} aria-label="Report settings">
          <IconSettings size={18} />
        </button>
      </Flex>

      <Box className={classes.kpiGrid}>
        <Box className={classes.kpi} onClick={() => onStatusChange("all")}>
          <Box className={classes.kpiArrow}><IconArrowRight size={12} stroke={2.2} /></Box>
          <Flex className={classes.kpiTop}>
            <Box className={cx(classes.kpiChip, classes.kpiChipAmber)}>
              <IconFileText size={17} />
            </Box>
            <Text className={classes.kpiLabel}>Quarterly Reports This Month</Text>
          </Flex>
          <Text className={classes.kpiValue}>{kpi.thisMonth}</Text>
          <Text className={classes.kpiSub}>Across {kpi.providerCount} providers</Text>
        </Box>

        <Box className={classes.kpi} onClick={onOpenPastDue}>
          <Box className={classes.kpiArrow}><IconArrowRight size={12} stroke={2.2} /></Box>
          <Flex className={classes.kpiTop}>
            <Box className={cx(classes.kpiChip, classes.kpiChipAmber)}>
              <IconAlertTriangle size={17} />
            </Box>
            <Text className={classes.kpiLabel}>Quarterly Reports Past Due</Text>
          </Flex>
          <Text className={classes.kpiValue}>{kpi.pastDue}</Text>
          <Text className={classes.kpiSub}>Longest {kpi.longestPastDueDays} days past the filing window</Text>
        </Box>

        <Box className={classes.kpi} onClick={onOpenFailed}>
          <Box className={classes.kpiArrow}><IconArrowRight size={12} stroke={2.2} /></Box>
          <Flex className={classes.kpiTop}>
            <Box className={cx(classes.kpiChip, classes.kpiChipRed)}>
              <IconCircleX size={17} />
            </Box>
            <Text className={classes.kpiLabel}>Failed Reports</Text>
          </Flex>
          <Text className={classes.kpiValue}>{kpi.failed}</Text>
          <Text className={classes.kpiSub}>Failed this month · retry to regenerate</Text>
        </Box>
      </Box>

      <Box className={classes.tableWrap}>
        <Flex className={classes.cardToolbar}>
          <Flex className={classes.segmented}>
            <button
              className={cx(classes.segmentedBtn, status === "all" && classes.segmentedBtnOn)}
              onClick={() => onStatusChange("all")}
            >
              All
            </button>
            <button
              className={cx(classes.segmentedBtn, status === "draft" && classes.segmentedBtnOn)}
              onClick={() => onStatusChange("draft")}
            >
              Awaiting Review
            </button>
          </Flex>
          <Flex className={classes.search}>
            <IconSearch size={15} />
            <input
              className={classes.searchInput}
              placeholder="Search client or provider"
              value={search}
              onChange={(e) => onSearchChange(e.target.value)}
            />
          </Flex>
        </Flex>

        {/* Rows already on screen are the pre-action ones — a report just
            signed, deleted or retried still shows its old status until the
            refetch lands. Cover them until the fresh statuses arrive rather
            than letting a stale pill read as the current one. */}
        <Box className={classes.tableScrollWrap}>
          <LoadingOverlay
            visible={!!refreshing}
            overlayBlur={1}
            overlayOpacity={0.6}
            zIndex={5}
            loaderProps={{ color: "violet", size: "sm" }}
          />
          <Box className={classes.tableScroll}>
            <table className={classes.table}>
              <thead>
                <tr>
                  <th>Client</th>
                  <th>Provider</th>
                  <th>Review Period</th>
                  <th>Source</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {pageRows.map((r) => (
                  <tr
                    key={r.id}
                    className={classes.rowClickable}
                    onClick={() => onOpenReport(r)}
                  >
                    <td className={classes.tdMain}>{r.client}</td>
                    <td>{r.provider}</td>
                    <td className={classes.num}>{r.period}</td>
                    <td>
                      <StatusPill config={REPORT_SOURCE[r.source] || REPORT_SOURCE.ai} />
                    </td>
                    <td>
                      <StatusPill config={REPORT_STATUS[r.status]} />
                    </td>
                    <td className={classes.num}>{r.created}</td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <RowActionMenu
                        status={r.status}
                        onView={() => onOpenReport(r)}
                        onDownload={() => onOpenReport(r, { download: true })}
                        onDelete={() => onDeleteReport?.(r)}
                        downloading={downloadingId === r.id}
                        deleting={deletingId === r.id}
                      />
                    </td>
                  </tr>
                ))}
                {pageRows.length === 0 && (
                  <tr>
                    <td colSpan={7} className={classes.emptyCell}>
                      No reports found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </Box>
        </Box>

        <Flex className={classes.cardPagination}>
          <Text className={classes.pagInfo}>
            Showing {pageRows.length === 0 ? 0 : (page - 1) * PAGE_SIZE + 1}–
            {Math.min((page - 1) * PAGE_SIZE + pageRows.length, totalReports || 0)} of {totalReports || 0} reports
          </Text>
          <Flex className={classes.pagBtns}>
            <button
              className={classes.pageBtn}
              disabled={page === 1}
              onClick={() => onPageChange(Math.max(1, page - 1))}
            >
              ‹
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((n) => (
              <button
                key={n}
                className={cx(classes.pageBtn, n === page && classes.pageBtnOn)}
                onClick={() => onPageChange(n)}
              >
                {n}
              </button>
            ))}
            <button
              className={classes.pageBtn}
              disabled={page === totalPages}
              onClick={() => onPageChange(Math.min(totalPages, page + 1))}
            >
              ›
            </button>
          </Flex>
        </Flex>
      </Box>
    </Box>
  );
};

export default ReportsListView;
